import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SchoolIcon from '@mui/icons-material/School';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { idpService } from '../services/idpService';
import { formatDate } from '../utils/dateFormat';
import type { IndividualDevelopmentPlan, EmployeeWithoutIdp } from '../types/idp';
import { IdpStatus } from '../types/idp';
import { formatIdpStatus, getIdpStatusColor } from '../utils/idpStatus';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, permissionCopy } from '../utils/userFacingCopy';

export default function AllIdpsPage() {
  const navigate = useNavigate();
  const { user, displayTimeZoneId } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('Administrator');
  const isSeniorManager = (user?.roles ?? []).includes('SeniorManager');
  const canAccessAllIdps = isAdmin || isSeniorManager;
  const [searchParams, setSearchParams] = useSearchParams();
  const [idps, setIdps] = useState<IndividualDevelopmentPlan[]>([]);
  const [employeesWithoutIdp, setEmployeesWithoutIdp] = useState<EmployeeWithoutIdp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'employeeName' | 'year' | 'status' | 'createdAt' | 'goals'>('year');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const urlDepartmentId = searchParams.get('departmentId');
  const urlDepartmentName = searchParams.get('departmentName');
  const urlStatusFilter = searchParams.get('status');

  const [selectedDepartment, setSelectedDepartment] = useState<string>(urlDepartmentId || urlDepartmentName || 'all');
  const [selectedStatus, setSelectedStatus] = useState<string>(urlStatusFilter || 'all');
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);

  // Select value must match a MenuItem value. Ensure we have an option with id === selectedDepartment (URL may supply it before load or with different casing than reference data).
  const departmentOptions = useMemo(() => {
    const list = [...departments];
    if (selectedDepartment !== 'all' && !list.some((d) => d.id === selectedDepartment)) {
      const byCasing = list.find((d) => d.id.toLowerCase() === selectedDepartment.toLowerCase() || d.name === selectedDepartment);
      list.push({
        id: selectedDepartment,
        name: byCasing?.name ?? urlDepartmentName ?? '—',
      });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [departments, selectedDepartment, urlDepartmentName]);

  const loadIdps = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [idpsResponse, employeesResponse, departmentsResponse] = await Promise.all([
        idpService.getAllIdps(),
        idpService.getEmployeesWithoutIdp(),
        apiService.get<{ id: string; name: string }[]>(API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS.BASE).catch(() => ({ data: [] })),
      ]);
      setIdps(idpsResponse);
      setEmployeesWithoutIdp(employeesResponse);

      const deptMap = new Map<string, { id: string; name: string }>();
      departmentsResponse.data.forEach((d) => deptMap.set(d.id, { id: d.id, name: d.name }));
      idpsResponse.forEach((idp) => {
        (idp.departmentIds ?? []).forEach((deptId, i) => {
          const name = idp.departmentNames?.[i];
          if (deptId && name && !deptMap.has(deptId)) deptMap.set(deptId, { id: deptId, name });
        });
      });
      employeesResponse.forEach((emp) => {
        (emp.departmentIds ?? []).forEach((deptId, i) => {
          const name = emp.departmentNames?.[i];
          if (deptId && name && !deptMap.has(deptId)) deptMap.set(deptId, { id: deptId, name });
        });
      });
      setDepartments(Array.from(deptMap.values()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('development plans')));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccessAllIdps) loadIdps();
    else setLoading(false);
  }, [loadIdps, canAccessAllIdps]);

  useEffect(() => {
    if (urlStatusFilter) setSelectedStatus(urlStatusFilter);
  }, [urlStatusFilter]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedDepartment !== 'all') {
      const dept = departments.find((d) => d.id === selectedDepartment || d.id.toLowerCase() === selectedDepartment.toLowerCase() || d.name === selectedDepartment);
      params.set('departmentId', dept?.id ?? selectedDepartment);
      if (dept?.name ?? urlDepartmentName) params.set('departmentName', dept?.name ?? urlDepartmentName ?? '');
    }
    if (selectedStatus !== 'all') params.set('status', selectedStatus);
    setSearchParams(params, { replace: true });
  }, [selectedDepartment, selectedStatus, departments, urlDepartmentName, setSearchParams]);

  /** Unified row for table: either IDP or employee without IDP (normalized). */
  const filteredIdps = useMemo(() => {
    if (selectedStatus === 'no-idp') {
      let list = employeesWithoutIdp;

      if (selectedDepartment !== 'all') {
        const dept = departments.find((d) => d.id === selectedDepartment || d.id.toLowerCase() === selectedDepartment.toLowerCase() || d.name === selectedDepartment);
        const deptId = dept?.id ?? selectedDepartment;
        list = list.filter((emp) => {
          const ids = emp.departmentIds ?? [];
          return ids.some((id) => id === deptId || id.toLowerCase() === deptId.toLowerCase());
        });
      }

      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        list = list.filter(
          (emp) =>
            (emp.employeeName ?? '').toLowerCase().includes(q) ||
            (emp.employeeEmail ?? '').toLowerCase().includes(q) ||
            String(emp.year).includes(q) ||
            (emp.departmentNames ?? []).some((n) => n.toLowerCase().includes(q))
        );
      }

      return list.map((emp) => ({
        id: `no-idp-${emp.employeeId}`,
        employeeId: emp.employeeId,
        employeeName: emp.employeeName,
        year: emp.year,
        status: 'NoIdp' as const,
        createdAt: undefined as string | undefined,
        developmentGoals: [] as IndividualDevelopmentPlan['developmentGoals'],
        departmentIds: emp.departmentIds,
        departmentNames: emp.departmentNames,
      }));
    }

    let list = idps;

    if (selectedDepartment !== 'all') {
      const dept = departments.find((d) => d.id === selectedDepartment || d.id.toLowerCase() === selectedDepartment.toLowerCase() || d.name === selectedDepartment);
      const deptId = dept?.id ?? selectedDepartment;
      list = list.filter((idp) => {
        const ids = idp.departmentIds ?? [];
        return ids.some((id) => id === deptId || id.toLowerCase() === deptId.toLowerCase());
      });
    }

    if (selectedStatus !== 'all') {
      if (selectedStatus === 'outstanding') {
        list = list.filter((idp) => {
          const s = idp.status;
          const isDraft = s === IdpStatus.Draft || s === 1 || s === 'Draft';
          const isWithManager = s === IdpStatus.WithManager || s === 2 || s === 'WithManager';
          return isDraft || isWithManager;
        });
      } else {
        const filterLabel = selectedStatus === 'withmanager' ? 'with manager' : selectedStatus.toLowerCase();
        list = list.filter((idp) => formatIdpStatus(idp.status).toLowerCase() === filterLabel);
      }
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (idp) =>
          (idp.employeeName ?? '').toLowerCase().includes(q) ||
          String(idp.year).includes(q) ||
          formatIdpStatus(idp.status).toLowerCase().includes(q) ||
          (idp.careerAspirations ?? '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [idps, employeesWithoutIdp, selectedDepartment, selectedStatus, searchTerm, departments]);

  const sortedIdps = useMemo(() => {
    return [...filteredIdps].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortField) {
        case 'employeeName':
          aVal = (a.employeeName ?? '').toLowerCase();
          bVal = (b.employeeName ?? '').toLowerCase();
          return sortDirection === 'asc'
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        case 'year':
          aVal = a.year;
          bVal = b.year;
          return sortDirection === 'asc' ? a.year - b.year : b.year - a.year;
        case 'status':
          aVal = formatIdpStatus(a.status);
          bVal = formatIdpStatus(b.status);
          return sortDirection === 'asc'
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        case 'createdAt':
          aVal = a.createdAt ?? '';
          bVal = b.createdAt ?? '';
          return sortDirection === 'asc'
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        case 'goals':
          aVal = a.developmentGoals?.length ?? 0;
          bVal = b.developmentGoals?.length ?? 0;
          return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
        default:
          return 0;
      }
    });
  }, [filteredIdps, sortField, sortDirection]);

  const paginatedIdps = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedIdps.slice(start, start + rowsPerPage);
  }, [sortedIdps, page, rowsPerPage]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </AppLayout>
    );
  }

  if (!canAccessAllIdps) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', py: 4 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {permissionCopy.allIdpsList}
            </Alert>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/organisation')}>
              Back to Organisation dashboard
            </Button>
          </Card>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: -0.5 }} title="Back" size="small">
              <ArrowBackIcon />
            </IconButton>
            <SchoolIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              All Individual Development Plans
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {selectedStatus === 'no-idp'
              ? `${filteredIdps.length} employee${filteredIdps.length !== 1 ? 's' : ''} without IDP`
              : `${filteredIdps.length} IDP${filteredIdps.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          View all Individual Development Plans across the organisation. Filter by department or status; open an IDP to view details (read-only).
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Search IDPs"
                placeholder="Search by employee, year, status, or career aspirations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Department</InputLabel>
                <Select value={selectedDepartment} label="Department" onChange={(e) => setSelectedDepartment(e.target.value)}>
                  <MenuItem value="all">All Departments</MenuItem>
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select value={selectedStatus} label="Status" onChange={(e) => setSelectedStatus(e.target.value)}>
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="no-idp">No IDP</MenuItem>
                  <MenuItem value="outstanding">Outstanding</MenuItem>
                  <MenuItem value="acknowledged">Acknowledged</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {(selectedDepartment !== 'all' || selectedStatus !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {selectedDepartment !== 'all' && (() => {
                  const dept = departments.find((d) => d.id === selectedDepartment || d.id.toLowerCase() === selectedDepartment.toLowerCase() || d.name === selectedDepartment);
                  const name = dept?.name ?? urlDepartmentName ?? null;
                  return (
                    <Chip
                      label={name != null ? `Department: ${name}` : 'Department filter applied'}
                      color="warning"
                      onDelete={() => setSelectedDepartment('all')}
                      size="small"
                    />
                  );
                })()}
                {selectedStatus !== 'all' && (
                  <Chip
                    label={`Status: ${selectedStatus === 'no-idp' ? 'No IDP' : selectedStatus === 'outstanding' ? 'Outstanding' : formatIdpStatus(selectedStatus === 'draft' ? 1 : selectedStatus === 'withmanager' ? 2 : selectedStatus === 'acknowledged' ? 3 : 4)}`}
                    color="info"
                    onDelete={() => setSelectedStatus('all')}
                    size="small"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer sx={{ borderTop: 0 }}>
              <Table key={displayTimeZoneId}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('employeeName')}>
                        <strong>Employee</strong>
                        {getSortIcon('employeeName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('year')}>
                        <strong>Fiscal Year</strong>
                        {getSortIcon('year')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('status')}>
                        <strong>Status</strong>
                        {getSortIcon('status')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('createdAt')}>
                        <strong>Created</strong>
                        {getSortIcon('createdAt')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('goals')}>
                        <strong>Goals</strong>
                        {getSortIcon('goals')}
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedIdps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, border: 'none' }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {selectedStatus === 'no-idp'
                            ? (searchTerm || selectedDepartment !== 'all' ? 'No employees without IDP match your search or filters.' : 'All employees have an IDP for the current year.')
                            : (searchTerm || selectedDepartment !== 'all' || selectedStatus !== 'all' ? 'No IDPs match your search or filters.' : 'No IDPs found.')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedIdps.map((idp) => (
                      <TableRow key={idp.id} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`/individual-development-plan/${idp.employeeId}/${idp.year}`)}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {idp.employeeName || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>{idp.year}</TableCell>
                        <TableCell>
                          <Chip label={formatIdpStatus(idp.status)} size="small" color={getIdpStatusColor(idp.status)} />
                        </TableCell>
                        <TableCell>{idp.createdAt ? formatDate(idp.createdAt, { includeTime: true }) : '—'}</TableCell>
                        <TableCell>{idp.developmentGoals?.length ?? 0}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/individual-development-plan/${idp.employeeId}/${idp.year}`)}
                            color="primary"
                            title="View IDP"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedIdps.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
            />
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
